import React, { useState, useCallback } from 'react'
import { Tooltip } from '@mui/material'
import useAxiosInstance from '../../hooks/useAxios'

const SectionWiseCreditDistribution = () => {
  const [sectionData, setSectionData] = useState({})
  const [allSections, setAllSections] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [error, setError] = useState(null)
  const [reportId, setReportId] = useState('')
  const [expandedSections, setExpandedSections] = useState({})
  const [expandedViews, setExpandedViews] = useState({})
  const [expandedTools, setExpandedTools] = useState({})
  const [loadedSections, setLoadedSections] = useState({})
  const axios = useAxiosInstance()

  const fetchAllSections = useCallback(async (repId) => {
    setLoading(true)
    setInitialLoad(false)
    setError(null)
    setSectionData({})
    setAllSections([])
    setExpandedSections({})
    setExpandedViews({})
    setLoadedSections({})

    try {
      const firstResponse = await axios.get('/wkl/credits/section_wise_credits_distribution/', {
        params: { report_id: repId }
      })

      const { all_sections: sections, data: firstData } = firstResponse.data

      const groupedData = {}
      const initialLoaded = {}

      if (firstData && firstData.length > 0) {
        firstData.forEach(view => {
          const section = view.l1
          if (!groupedData[section]) groupedData[section] = []
          groupedData[section].push(view)
        })
        initialLoaded[sections[0]] = true
      }

      setAllSections(sections || [])
      setSectionData(groupedData)
      setLoadedSections(initialLoaded)

      if (sections && sections.length > 0) {
        setExpandedSections({ [sections[0]]: true })
      }

      for (let i = 1; i < sections.length; i++) {
        const section = sections[i]
        try {
          const sectionResponse = await axios.get('/wkl/credits/section_wise_credits_distribution/', {
            params: { report_id: repId, section }
          })
          const sectionViews = sectionResponse.data.data || []
          setSectionData(prev => ({ ...prev, [section]: sectionViews }))
          setLoadedSections(prev => ({ ...prev, [section]: true }))
        } catch (sectionErr) {
          console.error(`Failed to load section: ${section}`, sectionErr)
          setLoadedSections(prev => ({ ...prev, [section]: true }))
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch credit distribution')
    } finally {
      setLoading(false)
    }
  }, [])

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const toggleView = (sectionIdx, viewIdx) => {
    const key = `${sectionIdx}-${viewIdx}`
    setExpandedViews(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleTool = (viewKey, toolId) => {
    const fullKey = `${viewKey}-${toolId}`
    setExpandedTools(prev => ({ ...prev, [fullKey]: !prev[fullKey] }))
  }

  const formatCredits = (credits) => {
    if (!credits && credits !== 0) return '0.00'
    return Number(credits).toFixed(2)
  }

  const truncateWithTooltip = (text, maxLength = 45) => {
    if (!text) return text
    if (text.length <= maxLength) {
      return (
        <Tooltip title={text} placement="top" arrow>
          <span className="cursor-default">{text}</span>
        </Tooltip>
      )
    }
    return (
      <Tooltip title={text} placement="top" arrow>
        <span className="cursor-help border-b border-dashed border-gray-400">{text.slice(0, maxLength)}...</span>
      </Tooltip>
    )
  }

  const PALETTE = ['#374151', '#4B5563', '#6B7280', '#9CA3AF', '#374151', '#6B7280', '#9CA3AF', '#4B5563']

  const getSharedCredits = (toolDist) => {
    let shared = 0
    toolDist?.forEach(tool => {
      tool.distribution?.forEach(dist => {
        if (dist.shared) shared += Number(dist.credits) || 0
      })
    })
    return shared
  }

  const getToolCredit = (distribution) => {
    return distribution?.reduce((sum, d) => sum + (Number(d.credits) || 0), 0) || 0
  }

  const getSectionCredits = (sectionViews) => {
    return sectionViews.reduce((sum, view) => sum + (Number(view.credits_in_this_view) || 0), 0)
  }

  const grandTotal = Object.values(sectionData).flat().reduce((sum, view) => sum + (Number(view.credits_in_this_view) || 0), 0)
  const totalViews = Object.values(sectionData).flat().length

  return (
    <div className="min-h-screen bg-white font-sans text-sm text-gray-900">
      <div className="max-w-6xl mx-auto px-3 py-3">
        {/* Report ID Input */}
        <div className="bg-white rounded-lg p-3 mb-3 border border-gray-200">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label htmlFor="report-id" className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                Report ID
              </label>
              <input
                id="report-id"
                type="text"
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && reportId) fetchAllSections(reportId) }}
                placeholder="Enter report ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
              />
            </div>
            <button
              onClick={() => fetchAllSections(reportId)}
              disabled={!reportId || loading}
              className="px-4 py-2 bg-gray-700 text-white rounded-md text-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Loading...' : 'Load Credits'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 mb-3">
            <p className="text-gray-700 m-0">{error}</p>
          </div>
        )}

        {/* Initial empty state */}
        {initialLoad && Object.keys(sectionData).length === 0 && (
          <div className="bg-white rounded-lg p-10 text-center border border-gray-200">
            <p className="text-gray-500 m-0">Enter a report ID and click Load Credits to view distribution</p>
          </div>
        )}

        {/* Content */}
        {!initialLoad && (
          <>
            {/* Top Bar Stats */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-xl font-semibold text-gray-700">{formatCredits(grandTotal)}</div>
                <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide">Total Credits</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-xl font-semibold text-gray-700">{allSections.length}</div>
                <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide">L1 Sections</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-xl font-semibold text-gray-700">{totalViews}</div>
                <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide">Views (L2)</div>
              </div>
            </div>

            {/* L1 Sections List */}
            <div>
              {allSections.map((section, idx) => {
                const color = PALETTE[idx % PALETTE.length]
                const views = sectionData[section] || []
                const sectionCredit = getSectionCredits(views)
                const isExpanded = expandedSections[section]
                const isLoaded = loadedSections[section]
                
                let totalSharedCredits = 0
                views.forEach((view, vi)=>{
                  const sharedCredit = getSharedCredits(view.tool_level_distribution)
                  totalSharedCredits+=sharedCredit
                })   
                

                return (
                  <div key={section} className="mb-2.5 border border-gray-200 rounded-xl overflow-hidden">
                    {/* L1 Header */}
                    <div
                      className={`l1-header flex items-center gap-2.5 px-3 py-2 cursor-pointer select-none transition-opacity ${isLoaded ? 'hover:bg-gray-50' : ''}`}
                      style={{ backgroundColor: '#f9f9f9', opacity: isLoaded ? 1 : 0.6 }}
                      onClick={() => isLoaded && toggleSection(section)}
                    >
                      {isLoaded ? (
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      ) : (
                        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-400 animate-pulse" />
                      )}
                      <span className="font-semibold text-sm flex-1 text-gray-800">{section}</span>
                      {isLoaded === false ? (
                        <span className="text-xs text-gray-400">Loading...</span>
                      ) : (
                        <span className="text-xs text-gray-500 flex items-center gap-3">
                          <span>{views.length} view{views.length !== 1 ? 's' : ''}</span>
                        </span>
                      )}
                      {isLoaded && <span className="font-semibold text-sm text-gray-700">Extra: {formatCredits(sectionCredit)} cr</span>}
                      {isLoaded && <span className="font-semibold text-sm text-gray-700">| Shared: {totalSharedCredits} cr</span>}
                      {isLoaded && <span className="text-xs text-gray-400 transition-transform" style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}>›</span>}
                      {!isLoaded && (
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                      )}
                    </div>

                    {/* Section Loading Skeleton */}
                    {!isLoaded && (
                      <div className="border-t border-gray-200 px-3 py-3">
                        <div className="flex items-center gap-2 text-gray-400 text-xs">
                          <div className="w-3 h-3 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
                          <span>Loading section data...</span>
                        </div>
                      </div>
                    )}

                    {/* L1 Body - Views */}
                    {isExpanded && isLoaded && views.length > 0 && (
                      <div className="border-t border-gray-200">
                        {views.map((view, vi) => {
                          const viewCredit = Number(view.credits_in_this_view) || 0
                          const viewKey = `${idx}-${vi}`
                          const isViewExpanded = expandedViews[viewKey]
                          const sharedCredit = getSharedCredits(view.tool_level_distribution)
                          const toolsWithData = view.tool_level_distribution?.filter(t => t.distribution?.length > 0) || []

                          return (
                            <div key={view.view_id} className="border-b border-gray-200 px-4 py-2 pl-5">
                              {/* View Header */}
                              <div
                                className="view-header flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                                onClick={() => toggleView(idx, vi)}
                              >
                                <span className="text-xs text-gray-400 font-mono w-9">{view.view_id}</span>
                                <span className="view-name text-xs font-medium flex-1 text-gray-700">{view.l2}</span>
                                <span className="text-xs font-semibold w-14 text-right text-gray-700">
                                  {formatCredits(viewCredit)} cr
                                  {sharedCredit > 0 && <span className="text-xs text-gray-500 font-normal"> ({formatCredits(sharedCredit)} shared)</span>}
                                </span>
                                <span className="text-xs text-gray-400 transition-transform" style={{ transform: isViewExpanded ? 'rotate(90deg)' : 'none' }}>›</span>
                              </div>

                              {/* View Body - Tool Distribution Table */}
                              {isViewExpanded && (
                                <div className="mt-2">
                                  {/* View Level Distribution */}
                                  {view.view_level_credits_distribution?.length > 0 && (
                                    <div className="mb-2">
                                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">View Level</p>
                                      <div className="flex flex-wrap gap-1">
                                        {view.view_level_credits_distribution.map((log) => (
                                          <span
                                            key={log.log_name}
                                            className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 font-mono"
                                          >
                                            {truncateWithTooltip(log.log_name)} = {formatCredits(log.credits)}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Tool Level Distribution */}
                                  {toolsWithData.length > 0 && (
                                    <div>
                                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Tool Level Distribution</p>
                                      <table className="w-full text-xs border-collapse">
                                        <thead>
                                          <tr>
                                            <th className="text-left py-0.5 px-1.5 font-medium text-gray-500 border-b border-gray-200 uppercase tracking-wide">Tool</th>
                                            <th className="text-left py-0.5 px-1.5 font-medium text-gray-500 border-b border-gray-200 uppercase tracking-wide">Task</th>
                                            <th className="text-right py-0.5 px-1.5 font-medium text-gray-500 border-b border-gray-200 uppercase tracking-wide">Credits</th>
                                            <th className="text-left py-0.5 px-1.5 font-medium text-gray-500 border-b border-gray-200 uppercase tracking-wide">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {view.tool_level_distribution?.map((tool) => {
                                            const toolCredit = getToolCredit(tool.distribution)
                                            const hasShared = tool.distribution?.some(d => d.shared) || false
                                            const viewKey = `${idx}-${vi}`
                                            const toolKey = `${viewKey}-${tool.tool_id}`
                                            const isToolExpanded = expandedTools[toolKey]
                                            const hasDistribution = tool.distribution && tool.distribution.length > 0

                                            return (
                                              <React.Fragment key={tool.tool_id}>
                                                <tr
                                                  className={`${toolCredit === 0 ? 'opacity-50' : ''} cursor-pointer hover:bg-gray-50`}
                                                  onClick={() => hasDistribution && toggleTool(viewKey, tool.tool_id)}
                                                >
                                                  <td className="py-1 px-1.5 border-b border-gray-200 align-top">
                                                    <div className="flex items-center gap-1">
                                                      {hasDistribution && (
                                                        <span className="text-xs text-gray-400">{isToolExpanded ? '▼' : '▶'}</span>
                                                      )}
                                                      <span className="font-medium text-gray-700">{tool.name}</span>
                                                      {hasShared && <span className="ml-1 text-xs px-1 py-0.5 rounded bg-gray-200 text-gray-600 border border-gray-300">shared</span>}
                                                    </div>
                                                  </td>
                                                  <td className="py-1 px-1.5 border-b border-gray-200 align-top text-gray-400 font-mono text-xs max-w-44 break-all">
                                                    {truncateWithTooltip(tool.task_name)}
                                                  </td>
                                                  <td className="py-1 px-1.5 border-b border-gray-200 text-right whitespace-nowrap">
                                                    <span className="font-semibold text-gray-700">{formatCredits(toolCredit)}</span>
                                                  </td>
                                                  <td className="py-1 px-1.5 border-b border-gray-200">
                                                    {hasShared ? <span className="text-gray-600">shared</span> : <span className="text-gray-400">—</span>}
                                                  </td>
                                                </tr>
                                                {isToolExpanded && hasDistribution && (
                                                  <tr>
                                                    <td colSpan={4} className="py-1 px-1.5 border-b border-gray-100 bg-gray-50">
                                                      <div className="pl-4">
                                                        <p className="text-xs text-gray-400 mb-1">Distribution:</p>
                                                        <div className="flex flex-wrap gap-1">
                                                          {tool.distribution.map((dist) => (
                                                            <span
                                                              key={dist.log_name}
                                                              className={`text-xs px-1.5 py-0.5 rounded border font-mono ${dist.shared ? 'bg-gray-200 border-gray-300 text-gray-700' : 'bg-gray-100 border-gray-200 text-gray-600'}`}
                                                            >
                                                              {truncateWithTooltip(dist.log_name)} = {formatCredits(dist.credits)}
                                                              {dist.shared && <span className="ml-1 text-gray-500">→ {dist.used_in}</span>}
                                                            </span>
                                                          ))}
                                                        </div>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                              </React.Fragment>
                                            )
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {toolsWithData.length === 0 && (
                                    <div className="py-1.5 text-xs text-gray-400">No tool distributions</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Empty section */}
                    {isExpanded && isLoaded && views.length === 0 && (
                      <div className="border-t border-gray-200 px-3 py-3 text-center text-gray-400 text-xs">
                        No views in this section
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* No data state */}
            {Object.keys(sectionData).length === 0 && !loading && (
              <div className="bg-white rounded-lg p-10 text-center border border-gray-200 mt-4">
                <p className="text-gray-500 m-0">No data available for this report</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SectionWiseCreditDistribution